<docs>
  # TheLoader
  > main loader screen AND page transition

  - this file is about graphic loader
  - the loading logic will be within its store

  ## logic

  - timelineEnterLoader
    - show loader elements (loading... / logo)
    - must be fast (0.2s)

  - timelineProgressLoader
    + long 10s - then speed up once loaded
    + start at the same time as timelineEnterLoader
    + at this stage we wait for :
      + app && globals && page all ready
      + loader queue (manifest etc)
      + 10s fallback (TIMEOUT)

  - **no loader config* 
    + the loader will use PageStransition as if shown - once all ready hide
    + same process than above - but speedup since no animations expected to be done before hiding loader
    + it is expected that if use no loader - pageTransition would be simple (like a fadeIn/fadeOut blank)

  - when all ready :
    + wait for enter / progress (speed up)
    + hide loader|pageTransition
  
  @author Melissa Martin <melissa@kffein.com>
  @author Nicolas Husson <nicolas@kffein.com>
</docs>

<script lang="ts">
import { promiseTimeout, tryOnMounted, until } from '@vueuse/core';
import scrollStore from 'components/SmoothScroll/store';
import store from 'components/ui/Loader/store';
// import soundStore from 'store/sound';
// import { gsap } from 'vendors/gsap'
// import { store as trackingStore } from 'store/tracking';
import { IS_DEV, ONCE_LOADER, SHOW_LOADER, cookies, motion } from 'config';
import Cookies from 'js-cookie';
import { clone } from 'lodash';
import appStore from 'plugins/store/app';
import useFlow from 'utils/flow';
import { computed, defineComponent, nextTick, ref, toRefs } from 'vue';

/** fake loader additional delay in second */
const FAKE_DELAY = 0 //@todo remove this when more webgl loaded
const TIMEOUT = 10

let gsap: any;
type Timeline = any
type LoaderTimelines = {
  noloader?: Timeline, // if no loader - this will be PageTransition already seek to 1 `enter` (shown)
  enter?: Timeline,
  progress?: Timeline
  leave: Timeline
}
const staticGsap = {
  fakeTween(options: Obj) {
    options?.onComplete?.()
  },
  tweenTo(label: string, options: Obj) {
    staticGsap.fakeTween(options)
  }
}

export default defineComponent({
  name: "TheLoader",
  setup() {
    const { loaded, type } = toRefs(store.state);
    const { is404 } = toRefs(appStore.state);

    const Loader = ref<any>(null)
    const Logo = ref<any>(null)
    const PreloadContent = ref<any>(null)
    const Progress = ref<any>(null)

    /** show loader bg only before pannel fill screen
     * then no bg so pannel will reveal page content */
    let showLoaderComputed = SHOW_LOADER
    // if `ONCE_LOADER` is activated and we have the cookie, force skip all the loader
    if (ONCE_LOADER /* && trackingStore.consents.preferences */ && Cookies.get(cookies.loader)) {
      showLoaderComputed = false;
    }

    const showBg = ref<boolean>(showLoaderComputed)

    // if (ONCE_LOADER && appStore.firstPageReady) {
    //   Cookies.set(cookies.loader, 'true');
    // }
    if (!showLoaderComputed)
      until(type).toBe('transition').then(() => showBg.value = false)


    let tlPageTransition = ref<Timeline>(undefined)


    /** ======================================================================================================
      * !! TIMELINES !! - animations are here.
    ====================================================================================================== **/
    /** Page Transition timelines in & out
     * > vs tlPageTransition
     * is 1 timeline @1s duration - 0.5 must be the end of enter (eg fill screen)
     * 
     * so onEnter = tweenTo 0.5|'enter' - onLeave = tweenTo 1|'leave'
     */
    function createPageTransitionTimeline() {
      if (!showLoaderComputed) return createNoLoaderTimeline()
      if (!motion) return staticGsap
      return gsap.timeline()

        .add('start')
        .add('enter')
        .add(() => { }, "+=0.05")
        .add('leave')
        .pause().seek('start')
    }

    /** 
     * if no loader aka no motion on first load - will use this timeline to fadeOut loader
     */
    function createNoLoaderTimeline() {
      if (!motion) return staticGsap
      return gsap.timeline()

        .add('start')
        .add('enter')
        .add(() => { }, "+=.2")
        .add('leave')
        .pause().seek('start')
    }

    /**
     * loader enter timeline is mandatory - cannot be skipped
     * then will play progress timeline that can be timescaled
     */
    function createLoaderTimelineEnter() {
      const tl = gsap.timeline()
        .add('start')
        .add(() => {
          //first animation loader (to acrivate the bacground new step)
          store.setRuntime();
        })

      if (!is404.value) {
        const ProgressEl = document.querySelector(".TheLoader .preload-infos");
        tl
          .set(ProgressEl, {
            y: -20,
            opacity: 0,
          }, 'start')
          .to(ProgressEl, {
            y: 0,
            opacity: 1,
            duration: 0.7,
            ease: 'back.out',
          }, 'start+=0.5')
      }

      tl.add('end');

      return tl;
    }
    /**
     * loader progress timeline is from 0 to 1
     * !important to have something here, since we watch the progress of that timeline, even if we tween something that doesn't move
     * but will be timeScalled to fake long/short progress (@todo)
     **/
    function createLoaderTimelineProgress() {
      return gsap.timeline()
        .add('start')
        .add( () => {}, "+=.2")
        // .to(progressPercent, { value: 100, duration: 1, ease: "linear.out" }, 'start')
        .add('end')
    }
    /** 
     * loader leave progress tween - can add a leave anim after to nicely hide our loader
     * leave tl will not be timeScalled 
     **/
    function createLoaderTimelineLeave() {
      return gsap.timeline({ paused: true })
        .add('start')
        .to(document.querySelector(".TheLoader .preload-infos"), {
          y: -20,
          opacity: 0,
          duration: 0.7,
          ease: 'back.in',
        }, 'start')
        .add(() => {
          showBg.value = false
          const TheLoader = document.querySelector('.TheLoader') as HTMLElement
          if (TheLoader) TheLoader.dataset.showBg = 'false'
        }, 'start+=0.7')
        .add('end')
    }

    /** ======================================================================================================
      * !IMPORTANT SETUP - complete logical flow, no anim here
    ====================================================================================================== **/

    /** Loader timelines
     * > vs tlPageTransition
     * we may prepare timelines so play them after
     */
    const tlLoader: LoaderTimelines = {
      noloader: undefined,
      enter: undefined,
      progress: undefined,
      leave: undefined,
    }
    const isLoaderEnterReady = ref(!showLoaderComputed)

    /* main loading flow : load manifest, wait app ready etc
    * wait for everithing needed then set loaded (store.setLoaded())
    * if was loader - switch to pageTransition mode
    */
    function load() {
      /** stack of loader promise
       * once each of theme are resolve - consider loader loaded */
      let promises: (Promise<any>)[] = [
        // load manifest.json
        store.loadManifest(),
        // app ready (router, globals, page etc)
        store.waitAppReady(),
      ]

      if (FAKE_DELAY && IS_DEV) {
        promises.push(promiseTimeout(FAKE_DELAY * 1000))
      }

      /** load loader queue
       * > you may add some things to preload here (manifest, some main lib, whatever)
       * at the end, may recheck to ensure queue is still empty before ready
       */
      function waitAllPromises(): Promise<any> {
        if (promises.length <= 0) return Promise.resolve()

        let p = clone(promises)
        promises = []
        return Promise.all(p)
          .then(waitAllPromises)
      }

      const { ready } = useFlow({
        load: waitAllPromises,
        debug: 'loader'
      })

      /** when flow ready - loader have loaded everythings etc
       * time to render page and leave this loader
       */
      Promise.race([
        until(ready).toBe(true, { timeout: TIMEOUT * 1000, throwOnTimeout: true }),
        until(is404).toBe(true)
      ])
        .catch(() => undefined)
        .then(() => {
          // If timeout => 404
          nextTick(() => {
            if (!ready.value && !is404.value) {
              appStore.setIs404();
            }
          })

          // This will trigger LOADED that will remove loader dom el (v-if)
          // then call `leave()` through vue's transition system
          tryOnMounted( async () => {
            await until(computed( () => store.allPreloaded)).toBe(true);
            store.setLoaded();
          })
        })
    }



    /** anim flow here
     * will wait for prev transition before
     * and skip all if not motion
     */
    function setupLoaderAnimFlow() {
      if (!motion) {
        gsap = staticGsap
        return
      }
      // so this loader will call the 1st loadAllGsap :)
      // exept if no motion then return a fake gsap
      import('vendors/gsap')
        .then(m => {
          gsap = m.gsap
          return gsap
        })
        .then(() => {
          tryOnMounted(() => {
            if (!showLoaderComputed) {
              tlLoader.enter = createNoLoaderTimeline().pause()
              isLoaderEnterReady.value = true
              return
            } else {
              tlLoader.leave = createLoaderTimelineLeave()
              tlLoader.enter = createLoaderTimelineEnter()
              tlLoader.progress = createLoaderTimelineProgress()
              // @ts-ignore
              // window.tl = tlLoader

              tlLoader.enter.seek('start').pause()
              tlLoader.progress.seek('start').pause()
            }

            setTimeout(() => {
              // waitTlComplete(tlLoader.enter, 'enter').then(() => {
              //   console.log("here-enter");
              //   isLoaderEnterReady.value = true
              //   // @tips : you may want to wait here before start progress
              // })

              //Play the enter timeline
              tlLoader.enter.eventCallback("onComplete", () => {
                isLoaderEnterReady.value = true
                tlLoader.progress
                  // .timeScale(0.1) // 10s loader - may be speed up once loaded
                  .play();
                load();
              });
              tlLoader.enter.play();
            }, 180)
          })
        })
    }
    setupLoaderAnimFlow()

    /** wait a tween to be done */
    function waitTlComplete(tl: Timeline, label?: string) { // eslint-disable-line @typescript-eslint/no-unused-vars
      return new Promise(resolve => {
        if (tl.progress() >= 0.99) return resolve(true)
        tl.eventCallback('onComplete', () => resolve(true));

        // eventCallback may bug if use GSDevTools
        if (IS_DEV) {
          [500, 1000, 1500, TIMEOUT * 1000, (TIMEOUT + 1) * 1000].forEach(delay => {
            setTimeout(() => {
              if (tl.progress() >= 0.99) return resolve(true)
            }, delay)
          });
        }
      })
    }

    /** called by vue trantition once loader done
     * @todo test if not motion - don't know if we even enter here serverside
     */
    const leaveLoader = (loader: Element, onComplete: () => void) => {
      if (!motion) { return onComplete() }

      // wait end of progress ti (and speed it up)
      function waitTlProgressEnd() {
        const wait = waitTlComplete(tlLoader.progress, 'progress')
        tlLoader.progress.timeScale(1);
        return wait
      }

      (() => {
        if (isLoaderEnterReady.value) {
          if (!showLoaderComputed) return Promise.resolve(onComplete())
            return waitTlProgressEnd()
          }
        return until(isLoaderEnterReady).toBe(true)
          // .then(waitTlProgressEnd) // @tips : comment this line if want skip progress here
      })()
        .then(() => {
          tlLoader[showLoaderComputed ? 'leave' : 'enter']
            ?.eventCallback('onComplete', onComplete)
            .play()
        })
    };

    /* vue transition enter - so when render on page
      - if loader - do nothing...
      - if pageTransition - waith for pageReady before setLoaded
    */
    function onEnter(el: Element, done: () => void) {
      store.setDone(false)
      scrollStore.setScrollable(false);
      if (type.value === 'loader') {
        return done()
      }
      tlPageTransition.value = createPageTransitionTimeline()
      tlPageTransition.value.tweenTo('enter', { onComplete: done })
      return new Promise((resolve) => {
        if (appStore.ready().value) return resolve(true)
        return resolve(until(appStore.ready()).toBe(true))
      })
        .then(() => {
          return store.setLoaded()
        })
    }

    /* vue transition leav - so when loader leave to reveal page
     * > leave loader (or pageTransition)
     * - page is already ready bellow - before we hide this loader
     * - once loader leave done - enable body scroll
    */
    function onLeave(el: Element, done: () => void) {
      function onComplete() {
        store.setDone()
        scrollStore.setScrollable(true);
        done()
      }

      if (!tlPageTransition.value) return leaveLoader(el, onComplete)
      tlPageTransition.value.tweenTo('leave', { onComplete: onComplete, ease: 'power0' })
    }
    const mounted = ref(false)
    tryOnMounted(() => { setTimeout(() => { mounted.value = true }, 200) })

    return {
      onEnter,
      onLeave,
      is404,
      showBg,
      type,
      loaded,
      mounted,
      percent: computed(() => {
        if(store.percentProgress.loaded === 0) return 0;
        return (store.percentProgress.loaded / store.percentProgress.total) * 100
      }),
      tlPageTransition,
      showLoaderComputed,
      Progress,
      // el refs
      ...{ Loader, Logo, PreloadContent },
    }
  }
});
</script>
<template v-if="mounted">
  <transition :css="false"
              name="loader"
              appear
              mode="in-out"
              @leave="onLeave"
              @enter="onEnter"
  >
    <!-- <template v-if="!loaded"> -->
    <!-- <TransitionPage v-if="type==='transition'" /> -->
    <div v-if="!loaded"
         ref="Loader"
         class="TheLoader"
         :data-bg="showBg"
         :data-noloader="!showLoaderComputed"
         :data-type="type"
         :data-loaded="loaded"
         :data-mounted="mounted"
    >
      <div ref="PreloadContent"
           :data-error="is404"
           class="preload-content"
      >
        <div class="preload-animation elmt" />
        <div ref="Progress" class="preload-infos">
          <p class="preload-progress elmt"
             v-html="`${Math.floor(percent)} %`"
          />
        </div>
      </div>
    </div>
    <!-- </template> -->
  </transition>
</template>

<style lang="stylus" scoped>
$z-loader ?= 1000
$z-transition ?= 500
.TheLoader
  fixed 0
  f 'center'
  pointer-events none
  background-color $c-rose
  // opacity 0.5!important
  opacity 1!important
  z-index $z-loader
  trans(background-color, 1s)
  &[data-noloader="true"]
    .background
      transform scaleY(1)
      background-color transparent
  &[data-mounted="false"]
    opacity 0 !important
  &[data-type="transition"]
    z-index $z-transition
    display none
  &[data-bg="false"]
    background-color transparent


  .preload-content
    f(center, $direction:column)
    color $c-main
    &[data-error="true"]
      pointer-events none
      visibility hidden

  .preload-infos
    absollute 0
    f(center, $direction:column)

    .preload-progress
      f-style('h1')
      font-family $ff-title_italic
      rp(font-size, 110px 110px)
      line-height 1
      margin-bottom 15px
            
</style>
